import React from 'react';
import * as Yup from 'yup';
import { AccountType, CreditPlan, PayedBy } from '../../../shared/Constants';
import { AccordionTab } from 'primereact/accordion';
import OwnerTermsOfUsePendingRequest from '../components/documents/OwnerTermsOfUsePendingRequest';
import DataProcessingAgreementPendingRequest from '../components/documents/DataProcessingAgreementPendingRequest';
import UserTermsOfUsePendingRequest from '../components/documents/UserTermsOfUsePendingRequest';
import RegularPendingRequests from '../components/documents/RegularPendingRequests';
import { isAccountOfNewBusinessModelType } from '../../../utils/AppUtil';

const isDocumentTypePresent = (requests, documentType) => {
  return !!requests.find((request) => request.documentType === documentType);
};

const DEFAULT_ACCOUNT_DATA = {
  id: 1,
  type: null,
  isHeadOfFamily: false,
  currentCreditPlan: CreditPlan.STANDARD,
  creditPlanExpirationDate: null,
  companyInformation: {
    country: null,
  },
  subscriptionInformation: {
    payedBy: PayedBy.PAYED_BY_SELF,
    hasActiveSubscription: false,
    currentSubscription: {
      endsOn: null,
      ninetyDaysAfterExpiration: null,
    },
  },
  gpiRepresentativeData: {
    parentAccountOwnerFullName: null,
    parentAccountOwnerEmail: null,
  },
  pendingDocumentChangeAcceptanceRequests: [],
};

const DEFAULT_DASHBOARD_DATA = {
  assessmentsByStatus: {
    NEW: 0,
    INVITED: 0,
    DECLINED: 0,
    STARTED: 0,
    SUBMITTED: 0,
    EXPIRED: 0,
  },
  assessmentsCount: 0,
  jobsByAssessmentTemplate: {},
  jobsCount: 0,
  respondentsCount: 0,
};

const DEFAULT_RESPONDENT_DATA = {
  externalRespondents: {
    presentlyUsed: 0,
    includedInPlan: null,
    deletionRule: {
      numberOfDaysToAnonymizeAfter: 90,
      type: 'AFTER_NUMBER_OF_DAYS',
    },
  },
  internalRespondents: {
    presentlyUsed: 0,
    includedInPlan: null,
    deletionRule: {
      numberOfDaysToAnonymizeAfter: 1,
      type: 'MANUAL',
    },
  },
};

export const DEFAULT_STATE = {
  accountData: DEFAULT_ACCOUNT_DATA,
  dashboard: DEFAULT_DASHBOARD_DATA,
  respondentData: DEFAULT_RESPONDENT_DATA,
  subscriptionExpiredDialogVisible: false,
};

export const buildDocumentsToAcceptInitialValues = () => {
  return {
    ownerTermsOfUseStatus: null,
    dataProcessingAgreementStatus: null,
    userTermsOfUseStatus: null,
  };
};

export const buildDocumentsToAcceptValidationSchema = (requests) => {
  const ownerTermsOfUseArePresent = isDocumentTypePresent(requests, 'OWNER_TERMS_OF_USE');
  const dataProcessingAgreementIsPresent = isDocumentTypePresent(requests, 'DATA_PROCESSING_AGREEMENT');
  const userTermsOfUseArePresent = isDocumentTypePresent(requests, 'USER_TERMS_OF_USE');

  return Yup.object().shape({
    ownerTermsOfUseStatus: Yup.string().when([], {
      is: () => ownerTermsOfUseArePresent === true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.nullable(),
    }),
    dataProcessingAgreementStatus: Yup.string().when([], {
      is: () => dataProcessingAgreementIsPresent === true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.nullable(),
    }),
    userTermsOfUseStatus: Yup.string().when([], {
      is: () => userTermsOfUseArePresent === true,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.nullable(),
    }),
  });
};

export const buildDocumentTabs = (pendingChangeRequests) => {
  const owner = pendingChangeRequests.filter((r) => r.documentType === 'OWNER_TERMS_OF_USE');
  const dpa = pendingChangeRequests.filter((r) => r.documentType === 'DATA_PROCESSING_AGREEMENT');
  const user = pendingChangeRequests.filter((r) => r.documentType === 'USER_TERMS_OF_USE');
  const regular = pendingChangeRequests.filter(
    (r) => !['OWNER_TERMS_OF_USE', 'DATA_PROCESSING_AGREEMENT', 'USER_TERMS_OF_USE'].includes(r.documentType),
  );

  const documentTabs = [];

  if (owner.length > 0) {
    documentTabs.push(
      <AccordionTab key="owner" header="Updated: Owner Terms of Use - Action Required">
        <OwnerTermsOfUsePendingRequest ownerTermsOfUsePendingChangeRequest={owner} />
      </AccordionTab>,
    );
  }

  if (dpa.length > 0) {
    documentTabs.push(
      <AccordionTab key="dpa" header="Updated: Data Processing Agreement - Action Required">
        <DataProcessingAgreementPendingRequest dataProcessingAgreementPendingChangeRequest={dpa} />
      </AccordionTab>,
    );
  }

  if (user.length > 0) {
    documentTabs.push(
      <AccordionTab key="user" header="User Terms of Use">
        <UserTermsOfUsePendingRequest userTermsOfUsePendingChangeRequest={user} />
      </AccordionTab>,
    );
  }

  if (regular.length > 0) {
    documentTabs.push(
      <AccordionTab key="regular" header="Other Documents">
        <RegularPendingRequests regularPendingChangeRequests={regular} />
      </AccordionTab>,
    );
  }

  return documentTabs;
};

export const getDocumentDisplayName = (type) => {
  switch (type) {
    case 'OWNER_TERMS_OF_USE':
      return 'Owner Terms of Use';
    case 'DATA_PROCESSING_AGREEMENT':
      return 'Data Processing Agreement';
    case 'USER_TERMS_OF_USE':
      return 'User Terms of Use';
    case 'GR8PI_RESPONDENT_TERMS_OF_USE':
      return 'Respondent Terms of Use';
    case 'GROW_RESPONDENT_TERMS_OF_USE':
      return 'GROW Respondent Terms of Use';
    case 'PRIVACY_POLICY':
      return 'Privacy Policy';
    case 'GR8PI_TERMS_OF_SERVICE':
      return 'GR8PI Terms of Service';
    case 'GROW_TERMS_OF_SERVICE':
      return 'GROW Terms of Service';
    case 'GR8PI_SERVICE_LEVEL_AGREEMENT':
      return 'GR8PI Service Level Agreement';
    case 'GR8PI_INTERNAL_SUPPORT_PROCEDURE':
      return 'GR8PI Internal Support Procedure';
    case 'GPI_PARTNER_AGREEMENT':
      return 'GPI Partner Agreement';
    default:
      return '';
  }
};

export const RESPONDENTS_SECTION_DEFAULT_STATE = {
  numberOfRespondentDeletionRequests: 0,
  numberOfInternalRespondents: 0,
  numberOfExternalRespondents: 0,
  numberOfInternalRespondentsInFamily: 0,
  numberOfExternalRespondentsInFamily: 0,
  internalRespondentsLeft: 0,
  respondentDeletionRules: {
    internalRespondentDeletionRule: {
      type: 'MANUAL',
      numberOfDaysToAnonymizeAfter: 0,
    },
    externalRespondentDeletionRule: {
      type: 'MANUAL',
      numberOfDaysToAnonymizeAfter: 0,
    },
  },
};

export const DEFAULT_QUESTIONNAIRE_SECTION_STATE = {
  assessments: {
    completedSinceLastLogin: 0,
    inProgress: 0,
    toExpireInTheNextSevenDays: 0,
    activeSuperLinks: 0,
  },
  jobProfileSurveys: {
    completedSinceLastLogin: 0,
    inProgress: 0,
    toExpireInTheNextSevenDays: 0,
  },
  surveys: {
    completedSinceLastLogin: 0,
    inProgress: 0,
    activeSuperSurveys: 0,
  },
};

export const SUBSCRIPTION_SECTION_DEFAULT_STATE = {
  maximumNumberOfInternalRespondents: 0,
  willBeRenewedOn: null,
};

export const CREDITS_SECTION_DEFAULT_STATE = {
  creditBalance: 0,
};

export const CONTACT_SECTION_DEFAULT_STATE = {
  ownerEmail: '',
  accountDataProtectionOfficerEmail: '',
  gpiContactEmail: '',
  gpiDataProtectionOfficerEmail: '',
};

export const getChangeOwnSubscriptionLinkClassName = (isHeadOfFamily) => {
  if (isHeadOfFamily) return 'underline black pointer margin-left-1';

  return 'disabled underline margin-left-1';
};

export const getChangeRespondentsDeletionRuleLinkClassName = (isHeadOfFamily) => {
  if (isHeadOfFamily) return 'paragraph black underline pointer';

  return 'paragraph underline disabled';
};

export const getGscDashboardPanelHeader = (type) => {
  const isNewBusinessModelType = isAccountOfNewBusinessModelType(type);
  const isEssentialAccount = type === AccountType.ESSENTIAL;

  if (isNewBusinessModelType) {
    return 'NoLimit';
  }

  if (isEssentialAccount) {
    return 'Essential';
  }

  return '';
};
